import { Loading } from 'components/core-v2/pages'
import { FormProvider, useForm } from 'react-hook-form'

import { MaxDrawdownReport } from '../../../api'
import { Card, CheckboxField, SelectField, Typography } from '../../../components/core-v2'
import {
  AssetDrawdownTable,
  PortfolioDrawdownTable
} from '../../../components/core-v2/organisms/asset-drawdown-table'

const FILTER_OPTIONS = [
  { label: 'Portfolios', value: 'portfolios' },
  { label: 'Assets', value: 'assets' }
]

const [PORTFOLIO_OPTION] = FILTER_OPTIONS

const CLOSE_TO_LIMIT_TOLERANCE = 0.03

export type MaximumDrawdownStatelessProps = {
  isLoading: boolean
  report: MaxDrawdownReport
}

export const MaximumDrawdownStateless = ({ isLoading, report }: MaximumDrawdownStatelessProps) => {
  const form = useForm({
    defaultValues: {
      filter: FILTER_OPTIONS[0].value,
      compliant: true,
      closeToLimit: true,
      breached: true
    }
  })
  const [filterValue, compliantValue, closeToLimitValue, breachedValue] = form.watch([
    'filter',
    'compliant',
    'closeToLimit',
    'breached'
  ])

  const isPortfolioFilter = filterValue === PORTFOLIO_OPTION.value

  let data = isPortfolioFilter ? report?.entries.ModelPortfolio : report?.entries.MutualFund
  const entriesCount = data.length
  data = data?.filter(row => {
    if (row.itemType === 'FundManager') {
      return true
    }

    const { maxDrawdown1D, maxDrawdown1M, maxDrawdown1Y, maxDrawdown3D } = row

    const gapPutStrike = row.gapPutStrike * -1

    const closeLimit = gapPutStrike - CLOSE_TO_LIMIT_TOLERANCE

    const breached =
      maxDrawdown1D >= gapPutStrike ||
      maxDrawdown3D >= gapPutStrike ||
      maxDrawdown1M >= gapPutStrike ||
      maxDrawdown1Y >= gapPutStrike

    const closeToLimit =
      maxDrawdown1D >= closeLimit ||
      maxDrawdown3D >= closeLimit ||
      maxDrawdown1M >= closeLimit ||
      maxDrawdown1Y >= closeLimit

    return (
      (breachedValue && breached) ||
      (closeToLimitValue && !breached && closeToLimit) ||
      (compliantValue && !breached && !closeToLimit)
    )
  })

  const reportCountMessage = `Showing ${data?.length} of ${entriesCount}`

  return (
    <FormProvider {...form}>
      {isLoading ? (
        <Loading className="mt-12" />
      ) : (
        <Card.Wrapper spacing="sm">
          <div className="d-flex">
            <SelectField id="filter" label="Filter" options={FILTER_OPTIONS} />
            <CheckboxField className="ml-auto mr-1" id="compliant" label="Compliant" />
            <CheckboxField className="mr-1" id="closeToLimit" label="Close to limit" />
            <CheckboxField id="breached" label="Breached" />
          </div>
          <Typography className="my-1" variant="sh3">
            {reportCountMessage}
          </Typography>
          {isPortfolioFilter ? (
            <PortfolioDrawdownTable data={data} />
          ) : (
            <AssetDrawdownTable data={data} />
          )}
        </Card.Wrapper>
      )}
    </FormProvider>
  )
}

export default MaximumDrawdownStateless
